import { ShareEvent } from "@/react/components/Events/NewEventsSpace/EventDetailPage/ShareEvent";
import { PostActionsDropdownMenu as PostActionsDropdownMenuV3 } from "@/react/components/PostsV3/PostViewListItem/PostActionsDropdownMenu";
import { PinnedToTopIcon } from "@circle-react/components/PostsV3/PostViewListItem/PinnedToTopIcon";
import { PostBody as PostBodyV3 } from "@circle-react/components/PostsV3/PostViewListItem/PostBody";
import { PostMetaInfo as PostMetaInfoV3 } from "@circle-react/components/PostsV3/PostViewListItem/PostMetaInfo";
import { PostName as PostNameV3 } from "@circle-react/components/PostsV3/PostViewListItem/PostName";
import { SpaceName as SpaceNameV3 } from "@circle-react/components/PostsV3/PostViewListItem/SpaceName";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import {
  useCurrentPostContext,
  useCurrentSpaceContext,
  usePunditUserContext,
} from "@circle-react/contexts";
import { dateIsPast } from "@circle-react/helpers/dateTimeHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { NewEventsEmbed } from "./NewEventsEmbed";

export interface MainContentProps {
  isRenderedOutsideTheSpace?: boolean;
  onPostChange: (post: any, options?: any) => void;
  onPostDestroy: (post: any) => void;
  pageSupportsContentTruncation?: boolean;
  refetchPosts: () => void;
  shouldHideShareLink: boolean;
}

export const MainContent = ({
  shouldHideShareLink,
  onPostChange,
  onPostDestroy,
  isRenderedOutsideTheSpace = false,
  pageSupportsContentTruncation = true,
  refetchPosts,
}: MainContentProps) => {
  const { record: post } = useCurrentPostContext();
  const { data: space } = useCurrentSpaceContext();

  const {
    name,
    is_truncation_disabled,
    flagged_for_approval_at,
    hide_meta_info,
    pinned_at_top_of_space,
  } = post;
  const doesPostSupportTruncation = !is_truncation_disabled;
  const { currentUser } = usePunditUserContext();
  const hasSupportForContentTruncation =
    pageSupportsContentTruncation && doesPostSupportTruncation;
  const { isSharedPostView, refetchPostDetails } = useCurrentPostHelpers();
  const { event_setting_attributes: eventData } = post;
  const { starts_at: startsAt } = eventData || {};
  const isSmScreen = useSmScreenMediaQuery();

  if (!post || !post.id) {
    return null;
  }

  const isEventFinished = !!startsAt && dateIsPast(startsAt);

  const containerClassName = classNames(
    "post post--parent event-item--post-view !bg-primary !border-primary mb-5 md:mb-6 rounded-none border first:mt-5 first:md:mt-0 md:rounded-2xl",
    {
      "post--shared": isSharedPostView,
      "post--shared__truncated": isSharedPostView && !currentUser,
      "is-truncation-disabled": !hasSupportForContentTruncation,
      "flagged-for-approval": flagged_for_approval_at,
      "post-name--empty": !name,
      "post-meta--hidden": hide_meta_info,
      "is-pinned": pinned_at_top_of_space,
      "event-item--event-ended": isEventFinished,
    },
  );

  const onChange = (updates = {}, options = {}) => {
    onPostChange({ ...post, ...updates }, options);
  };

  return (
    <div className={containerClassName}>
      <div className="event-item--post-view__post-header">
        <div className="post__post">
          <SpaceNameV3 space={space} />
          <div className="post__content flex flex-col gap-5 p-5 md:px-6">
            {isSmScreen && (
              <PinnedToTopIcon post={post} mobileWrapperClassName="!mb-0" />
            )}
            {Boolean(isRenderedOutsideTheSpace) && (
              <div
                className={classNames("flex items-start", {
                  "justify-between": !hide_meta_info,
                  "justify-end": hide_meta_info,
                })}
              >
                <PostMetaInfoV3
                  isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
                  space={space}
                />
                <div className="post__actions-container flex !flex-row items-center gap-0.5">
                  {!isSmScreen && (
                    <BookmarkButton
                      bookmarkType="event"
                      record={post}
                      onSuccess={refetchPostDetails}
                    />
                  )}
                  {!shouldHideShareLink && !isSmScreen && (
                    <ShareEvent onChange={onChange} />
                  )}
                  <PostActionsDropdownMenuV3
                    onChange={onChange}
                    onPostDestroy={onPostDestroy}
                    refetchPosts={refetchPosts}
                  />
                  {!isSmScreen && <PinnedToTopIcon post={post} />}
                </div>
              </div>
            )}
            {Boolean(!isRenderedOutsideTheSpace) && (
              <>
                <PostNameV3 />
                <PostMetaInfoV3
                  isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
                  space={space}
                />
              </>
            )}
            <PostBodyV3 enableTruncation={hasSupportForContentTruncation} />
          </div>
        </div>
      </div>
      <NewEventsEmbed event={post} onChange={onChange} />
    </div>
  );
};
